import { useState } from 'react';

interface RentalBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalType: 'daily' | 'weekly' | 'monthly';
}

export default function RentalBookingModal({ isOpen, onClose, rentalType }: RentalBookingModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    vehicleType: '',
    startDate: '',
    duration: '',
    message: '',
    // Contract information
    dateOfBirth: '',
    licenseNumber: '',
    licenseExpiry: '',
    emergencyContact: '',
    emergencyPhone: '',
    businessLicense: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiry: ''
  });

  const [uploadedFiles, setUploadedFiles] = useState({
    driverLicense: null as File | null,
    insurance: null as File | null,
    businessLicense: null as File | null,
    additionalDocs: null as File | null,
    creditCardFront: null as File | null,
    creditCardBack: null as File | null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [paymentOption, setPaymentOption] = useState<'inquiry' | 'subscription' | 'deposit' | 'custom'>('inquiry');
  const [customAmount, setCustomAmount] = useState('');
  const [showContractSection, setShowContractSection] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCustomAmount(value);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, fileType: keyof typeof uploadedFiles) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload only JPG, PNG, or PDF files');
        return;
      }

      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: file
      }));
    }
  };

  const removeFile = (fileType: keyof typeof uploadedFiles) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fileType]: null
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });
      formDataToSubmit.append('rentalType', rentalType);

      const response = await fetch('https://readdy.ai/api/form/d3sgkpjp0n2ns5fk79tg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSubmit as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          vehicleType: '',
          startDate: '',
          duration: '',
          message: '',
          dateOfBirth: '',
          licenseNumber: '',
          licenseExpiry: '',
          emergencyContact: '',
          emergencyPhone: '',
          businessLicense: '',
          insuranceProvider: '',
          insurancePolicyNumber: '',
          insuranceExpiry: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPaymentAmount = () => {
    if (paymentOption === 'custom') {
      return Number(customAmount) || 0;
    } else if (paymentOption === 'deposit') {
      return 1000; // Fixed deposit
    } else if (paymentOption === 'subscription') {
      return monthlyPrice;
    }
    return 0;
  };

  const handlePaymentSubmit = async () => {
    setIsSubmitting(true);

    try {
      console.log('Starting payment submission...');

      if (paymentOption === 'custom') {
        const amount = Number(customAmount);
        if (!amount || amount <= 0) {
          alert('Please enter a valid payment amount');
          setIsSubmitting(false);
          return;
        }
      }
      
      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const paymentAmount = getPaymentAmount();
      const requestData = {
        vehicleId: `rental-${rentalType}-${Date.now()}`,
        vehicleTitle: `${rentalType.charAt(0).toUpperCase() + rentalType.slice(1)} Rental - ${formData.vehicleType}`,
        rentalPrice: rentalType === 'daily' ? 199 : rentalType === 'weekly' ? 599 : 2599,
        duration: formData.duration === 'flexible' ? 1 : parseInt(formData.duration?.split('-')[0] || '1'),
        customerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company
        },
        paymentType: paymentOption,
        customAmount: paymentOption === 'custom' ? paymentAmount : undefined,
        amount: paymentAmount
      };

      console.log('Sending request:', requestData);

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-rental-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Payment error response:', errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Payment response data:', data);

      if (data.type === 'inquiry') {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else if (data.url) {
        console.log('Redirecting to Stripe:', data.url);
        // Force immediate redirect using window.open with _self
        window.open(data.url, '_self');
      } else {
        console.error('No payment URL received:', data);
        throw new Error('No payment URL received from server');
      }
    } catch (error) {
      console.error('Payment error:', error);
      
      if (error.name === 'AbortError') {
        alert('Payment setup timed out. Please check your connection and try again.');
      } else if (error.message.includes('Failed to fetch')) {
        alert('Network error. Please check your internet connection and try again.');
      } else {
        alert('Payment setup failed: ' + error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate rental pricing
  const baseRates = {
    daily: 199,
    weekly: 599,
    monthly: 2599
  };

  let totalAmount = baseRates[rentalType];
  
  if (rentalType === 'daily' && formData.duration) {
    const dayMultiplier = {
      '1-day': 1,
      '2-days': 2,
      '3-days': 3,
      '4-days': 4,
      '5-days': 5,
      '6-days': 6,
      '7-days': 7
    };
    totalAmount = totalAmount * (dayMultiplier[formData.duration as keyof typeof dayMultiplier] || 1);
  }

  if (rentalType === 'weekly' && formData.duration) {
    const weekMultiplier = {
      '1-week': 1,
      '2-weeks': 2,
      '3-weeks': 3,
      '4-weeks': 4
    };
    totalAmount = totalAmount * (weekMultiplier[formData.duration as keyof typeof weekMultiplier] || 1);
  }

  if (rentalType === 'monthly' && formData.duration) {
    const monthMultiplier = {
      '1-month': 1,
      '2-months': 2,
      '3-months': 3,
      '6-months': 6,
      '12-months': 12
    };
    totalAmount = totalAmount * (monthMultiplier[formData.duration as keyof typeof monthMultiplier] || 1);
  }

  // Calculate Quebec taxes
  const gst = totalAmount * 0.05;
  const qst = totalAmount * 0.09975;
  const totalTaxes = gst + qst;
  const totalWithTaxes = totalAmount + totalTaxes;

  // Calculate monthly price for recurring billing
  const monthlyBasePrice = rentalType === 'monthly' ? baseRates.monthly : rentalType === 'weekly' ? baseRates.weekly * 4.33 : baseRates.daily * 30;
  const monthlyGst = monthlyBasePrice * 0.05;
  const monthlyQst = monthlyBasePrice * 0.09975;
  const monthlyPrice = monthlyBasePrice + monthlyGst + monthlyQst;

  // Simple deposit amount
  const depositAmount = 1000; // Fixed $1,000 deposit

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              Book {rentalType === 'daily' ? 'Daily' : rentalType === 'weekly' ? 'Weekly' : 'Monthly'} Rental
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            Fill out the form below and choose your preferred payment option.
          </p>
        </div>

        {/* Payment Option Toggle */}
        <div className="p-6 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-3">Payment Options</h3>
          <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-lg mb-4">
            <button
              onClick={() => setPaymentOption('inquiry')}
              className={`py-2 px-3 rounded-md font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm ${
                paymentOption === 'inquiry'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Submit Inquiry
            </button>
            <button
              onClick={() => setPaymentOption('subscription')}
              className={`py-2 px-3 rounded-md font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm ${
                paymentOption === 'subscription'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly Billing
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setPaymentOption('deposit')}
              className={`py-2 px-3 rounded-md font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm ${
                paymentOption === 'deposit'
                  ? 'bg-white text-green-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Pay Standard Deposit
            </button>
            <button
              onClick={() => setPaymentOption('custom')}
              className={`py-2 px-3 rounded-md font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm ${
                paymentOption === 'custom'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Custom Amount
            </button>
          </div>
          
          {/* Payment Option Descriptions */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            {paymentOption === 'inquiry' && (
              <div className="text-sm text-gray-700">
                <strong>Submit Inquiry:</strong> Send us your rental request and we'll contact you within 24 hours to discuss details and arrange payment.
              </div>
            )}
            {paymentOption === 'deposit' && (
              <div className="text-sm text-gray-700">
                <strong>Standard Deposit:</strong> Pay a $1,000 deposit to secure your rental. Monthly payments will be set up separately.
              </div>
            )}
            {paymentOption === 'subscription' && (
              <div className="text-sm text-gray-700">
                <strong>Monthly Billing:</strong> Set up automatic monthly payments. You'll be charged the first month now, then automatically each month thereafter.
              </div>
            )}
            {paymentOption === 'custom' && (
              <div className="text-sm text-gray-700">
                <strong>Custom Amount:</strong> Pay any amount you want - from a small deposit to multiple months upfront. You choose how much to pay now.
              </div>
            )}
          </div>

          {/* Custom Amount Input */}
          {paymentOption === 'custom' && (
            <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Your Payment Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="text"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-semibold"
                />
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <p>• Minimum: $500</p>
                <p>• Maximum: ${Math.round(monthlyPrice * 12).toLocaleString()} (12 months upfront)</p>
                <p>• Pay any amount you're comfortable with</p>
              </div>
              {customAmount && Number(customAmount) > 0 && (
                <div className="mt-3 p-3 bg-white rounded border">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Your Payment:</span>
                    <span className="font-bold text-lg text-purple-600">
                      ${Number(customAmount).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    This covers approximately {Math.round(Number(customAmount) / monthlyPrice * 10) / 10} months of rental
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <form onSubmit={paymentOption === 'inquiry' ? handleFormSubmit : (e) => { e.preventDefault(); handlePaymentSubmit(); }} className="p-6" data-readdy-form>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter your first name"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter your last name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter your email address"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter your phone number"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter your company name"
              />
            </div>

            <div>
              <label htmlFor="vehicleType" className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle Type
              </label>
              <select
                id="vehicleType"
                name="vehicleType"
                value={formData.vehicleType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="">Select vehicle type</option>
                <option value="cargo-van">Cargo Van</option>
                <option value="box-truck-small">Box Truck (Small)</option>
                <option value="box-truck-medium">Box Truck (Medium)</option>
                <option value="box-truck-large">Box Truck (Large)</option>
                <option value="reefer-truck">Reefer Truck</option>
                <option value="tailgate-truck">Tailgate Truck</option>
                <option value="ramp-truck">Ramp Truck</option>
              </select>
            </div>

            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Start Date
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-2">
                Duration
              </label>
              <select
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="">Select duration</option>
                {rentalType === 'daily' ? (
                  <>
                    <option value="1-day">1 Day</option>
                    <option value="2-days">2 Days</option>
                    <option value="3-days">3 Days</option>
                    <option value="4-days">4 Days</option>
                    <option value="5-days">5 Days</option>
                    <option value="6-days">6 Days</option>
                    <option value="7-days">7 Days</option>
                    <option value="flexible">Flexible</option>
                  </>
                ) : rentalType === 'weekly' ? (
                  <>
                    <option value="1-week">1 Week</option>
                    <option value="2-weeks">2 Weeks</option>
                    <option value="3-weeks">3 Weeks</option>
                    <option value="4-weeks">4 Weeks</option>
                    <option value="flexible">Flexible</option>
                  </>
                ) : (
                  <>
                    <option value="1-month">1 Month</option>
                    <option value="2-months">2 Months</option>
                    <option value="3-months">3 Months</option>
                    <option value="6-months">6 Months</option>
                    <option value="12-months">12 Months</option>
                    <option value="flexible">Flexible</option>
                  </>
                )}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Requirements
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              placeholder="Tell us about any specific requirements, preferred features, or questions you have..."
            />
            <div className="text-right text-sm text-gray-500 mt-1">
              {formData.message.length}/500 characters
            </div>
          </div>

          {/* Contract & Documentation Section */}
          <div className="mt-8 border-2 border-blue-200 bg-blue-50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-900">Contract & Documentation</h3>
                <p className="text-blue-700 text-sm">Complete your rental documentation (all fields optional)</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-4 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-blue-600 mt-1"></i>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Documentation Information:</p>
                    <ul className="space-y-1">
                      <li>• Driver's license (recommended for faster processing)</li>
                      <li>• Proof of insurance (commercial insurance for business use)</li>
                      <li>• Business license (for commercial rentals)</li>
                      <li>• All documents help us process your rental faster</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-white p-6 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-user-line text-blue-600"></i>
                  Personal Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-2">Driver's License Number</label>
                    <input
                      type="text"
                      id="licenseNumber"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="License number"
                    />
                  </div>

                  <div>
                    <label htmlFor="licenseExpiry" className="block text-sm font-medium text-gray-700 mb-2">License Expiry Date</label>
                    <input
                      type="date"
                      id="licenseExpiry"
                      name="licenseExpiry"
                      value={formData.licenseExpiry}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="businessLicense" className="block text-sm font-medium text-gray-700 mb-2">Business License Number</label>
                    <input
                      type="text"
                      id="businessLicense"
                      name="businessLicense"
                      value={formData.businessLicense}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Business license number (if applicable)"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-white p-6 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-phone-line text-blue-600"></i>
                  Emergency Contact
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="emergencyContact" className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact Name</label>
                    <input
                      type="text"
                      id="emergencyContact"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Full name of emergency contact"
                    />
                  </div>

                  <div>
                    <label htmlFor="emergencyPhone" className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact Phone</label>
                    <input
                      type="tel"
                      id="emergencyPhone"
                      name="emergencyPhone"
                      value={formData.emergencyPhone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Emergency contact phone number"
                    />
                  </div>
                </div>
              </div>

              {/* Insurance Information */}
              <div className="bg-white p-6 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-blue-600"></i>
                  Insurance Information
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="insuranceProvider" className="block text-sm font-medium text-gray-700 mb-2">Insurance Provider</label>
                    <input
                      type="text"
                      id="insuranceProvider"
                      name="insuranceProvider"
                      value={formData.insuranceProvider}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Insurance company name"
                    />
                  </div>

                  <div>
                    <label htmlFor="insurancePolicyNumber" className="block text-sm font-medium text-gray-700 mb-2">Policy Number</label>
                    <input
                      type="text"
                      id="insurancePolicyNumber"
                      name="insurancePolicyNumber"
                      value={formData.insurancePolicyNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Insurance policy number"
                    />
                  </div>

                  <div>
                    <label htmlFor="insuranceExpiry" className="block text-sm font-medium text-gray-700 mb-2">Insurance Expiry</label>
                    <input
                      type="date"
                      id="insuranceExpiry"
                      name="insuranceExpiry"
                      value={formData.insuranceExpiry}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Document Upload Section */}
              <div className="bg-white p-6 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-upload-cloud-2-line text-blue-600"></i>
                  Document Upload
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Driver's License Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Driver's License <span className="text-gray-500">(JPG, PNG, PDF - Max 5MB)</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                      {uploadedFiles.driverLicense ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-file-text-line text-blue-600"></i>
                            <span className="text-sm text-gray-700 truncate">
                              {uploadedFiles.driverLicense.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('driverLicense')}
                            className="text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center">
                          <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600">Click to upload driver's license</p>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => handleFileUpload(e, 'driverLicense')}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Insurance Document Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Insurance Certificate <span className="text-gray-500">(JPG, PNG, PDF - Max 5MB)</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                      {uploadedFiles.insurance ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-file-text-line text-blue-600"></i>
                            <span className="text-sm text-gray-700 truncate">
                              {uploadedFiles.insurance.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('insurance')}
                            className="text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center">
                          <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600">Click to upload insurance certificate</p>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => handleFileUpload(e, 'insurance')}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Credit Card Front Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Credit Card (Front) <span className="text-gray-500">(JPG, PNG - Max 5MB)</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                      {uploadedFiles.creditCardFront ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-bank-card-line text-blue-600"></i>
                            <span className="text-sm text-gray-700 truncate">
                              {uploadedFiles.creditCardFront.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('creditCardFront')}
                            className="text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center">
                          <i className="ri-bank-card-line text-3xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600">Click to upload credit card front</p>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={(e) => handleFileUpload(e, 'creditCardFront')}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Credit Card Back Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Credit Card (Back) <span className="text-gray-500">(JPG, PNG - Max 5MB)</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                      {uploadedFiles.creditCardBack ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-bank-card-line text-blue-600"></i>
                            <span className="text-sm text-gray-700 truncate">
                              {uploadedFiles.creditCardBack.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('creditCardBack')}
                            className="text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center">
                          <i className="ri-bank-card-line text-3xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600">Click to upload credit card back</p>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={(e) => handleFileUpload(e, 'creditCardBack')}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Business License Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business License <span className="text-gray-500">(JPG, PNG, PDF - Max 5MB)</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                      {uploadedFiles.businessLicense ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-file-text-line text-blue-600"></i>
                            <span className="text-sm text-gray-700 truncate">
                              {uploadedFiles.businessLicense.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('businessLicense')}
                            className="text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center">
                          <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600">Click to upload business license</p>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => handleFileUpload(e, 'businessLicense')}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Additional Documents Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Documents <span className="text-gray-500">(JPG, PNG, PDF - Max 5MB)</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                      {uploadedFiles.additionalDocs ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-file-text-line text-blue-600"></i>
                            <span className="text-sm text-gray-700 truncate">
                              {uploadedFiles.additionalDocs.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('additionalDocs')}
                            className="text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center">
                          <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600">Click to upload additional documents</p>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => handleFileUpload(e, 'additionalDocs')}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start gap-2">
                    <i className="ri-shield-check-line text-green-600 mt-1"></i>
                    <div className="text-sm text-green-700">
                      <p className="font-medium mb-1">Document Security:</p>
                      <p>All uploaded documents are encrypted and stored securely. We only use them for rental verification and will never share them with third parties.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          {(paymentOption === 'subscription' || paymentOption === 'deposit' || paymentOption === 'custom') && (
            <div className="mt-6 bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">
                {paymentOption === 'deposit' ? 'Deposit Payment Summary' 
                : paymentOption === 'custom' ? 'Custom Payment Summary'
                : 'Monthly Payment Summary'} (Quebec)
              </h4>
              <div className="space-y-2 text-sm">
                {paymentOption === 'deposit' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rental Deposit:</span>
                      <span className="font-semibold">${depositAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-900 font-semibold">Total Deposit:</span>
                      <span className="font-bold text-lg">${depositAmount.toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-2">
                      * Deposit secures your rental. Monthly payments will be set up separately.
                    </div>
                  </>
                ) : paymentOption === 'custom' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Your Custom Payment:</span>
                      <span className="font-semibold">${(Number(customAmount) || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-900 font-semibold">Total Payment:</span>
                      <span className="font-bold text-lg">${(Number(customAmount) || 0).toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-2">
                      * Pay any amount you want. We'll arrange the rental terms based on your payment.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Rate:</span>
                      <span className="font-semibold">${monthlyBasePrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">GST (5%):</span>
                      <span className="font-semibold">${monthlyGst.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">QST (9.975%):</span>
                      <span className="font-semibold">${monthlyQst.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-900 font-semibold">Monthly Total:</span>
                      <span className="font-bold text-lg">${monthlyPrice.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Payment Information */}
          {paymentOption === 'custom' && (
            <div className="mt-4 bg-purple-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-purple-600 mt-1"></i>
                <div className="text-sm text-purple-800">
                  <p className="font-medium mb-1">Custom Payment Information:</p>
                  <ul className="space-y-1">
                    <li>• Pay exactly what you want - ${customAmount || '0'} CAD</li>
                    <li>• Flexible terms based on your payment amount</li>
                    <li>• No fixed schedules - pay on your terms</li>
                    <li>• Perfect for businesses with varying cash flow</li>
                    <li>• We'll contact you to arrange the rental details</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {paymentOption === 'deposit' && (
            <div className="mt-4 bg-green-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-green-600 mt-1"></i>
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">Deposit Payment Information:</p>
                  <ul className="space-y-1">
                    <li>• Pay $1,000 deposit to secure your rental</li>
                    <li>• We'll contact you to set up monthly payment plan</li>
                    <li>• Deposit will be applied to your final payment</li>
                    <li>• Simple and straightforward process</li>
                    <li>• No additional fees or taxes on deposit</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {paymentOption === 'subscription' && (
            <div className="mt-4 bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-blue-600 mt-1"></i>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Monthly Billing Information:</p>
                  <ul className="space-y-1">
                    <li>• You will be charged <strong>${monthlyPrice.toFixed(2)} CAD per month</strong></li>
                    <li>• Automatic billing on the same date each month</li>
                    <li>• {formData.duration || 'Selected'} rental contract</li>
                    <li>• Cancel anytime with 30 days notice</li>
                    <li>• Includes Quebec taxes (GST + QST)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800">
                <i className="ri-check-circle-line"></i>
                <span className="font-medium">Request submitted successfully!</span>
              </div>
              <p className="text-green-700 text-sm mt-1">
                We'll contact you within 24 hours to confirm your rental details.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <i className="ri-error-warning-line"></i>
                <span className="font-medium">Error submitting request</span>
              </div>
              <p className="text-red-700 text-sm mt-1">
                Please try again or contact us directly at (555) 123-4567.
              </p>
            </div>
          )}

          <div className="flex gap-4 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={isSubmitting || (paymentOption === 'custom' && (!customAmount || Number(customAmount) <= 0))}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
                paymentOption === 'inquiry' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : paymentOption === 'deposit'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : paymentOption === 'custom'
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Processing...
                </span>
              ) : (
                paymentOption === 'inquiry' 
                  ? 'Submit Inquiry'
                  : paymentOption === 'deposit'
                  ? `Pay Deposit - $${depositAmount.toFixed(2)}`
                  : paymentOption === 'custom'
                  ? `Pay $${(Number(customAmount) || 0).toFixed(2)}`
                  : `Start Monthly Billing - $${monthlyPrice.toFixed(2)}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
